import React, { useState, useEffect, useCallback, useRef } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import {
  Folder, FolderPlus, Upload, File as FileIcon, FileText, Image as ImageIcon,
  Film, Music, Search, LayoutGrid, List as ListIcon, MoreVertical, Share2,
  Trash2, Pencil, Download, FolderInput, ChevronRight, X, HardDrive, Link2,
  Copy, Check, Shield, Eye, Loader2, Archive, ArrowLeft,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";

const CHUNK = 5 * 1024 * 1024;
const ACCENT = { imagen: "#2563EB", video: "#DB2777", audio: "#7C3AED", documento: "#DC2626", texto: "#059669", comprimido: "#D97706", archivo: "#64748B" };

function fmtSize(b) {
  b = Number(b || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
}
function fileIconFor(tipo) {
  return { imagen: ImageIcon, video: Film, audio: Music, documento: FileText, texto: FileText, comprimido: Archive }[tipo] || FileIcon;
}

export default function DriveAdmin() {
  const [me, setMe] = useState(null);
  const [denied, setDenied] = useState(false);
  const [path, setPath] = useState([{ id: "", nombre: "Supera Drive" }]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [usage, setUsage] = useState(null);
  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]);
  const fileInput = useRef(null);

  // dialogs
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [moveTarget, setMoveTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);

  const cur = path[path.length - 1].id;

  const load = useCallback(async (folderId, q = "", tipo = "") => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      else params.folder_id = folderId;
      if (tipo) params.tipo = tipo;
      const [fo, fi, us] = await Promise.all([
        q ? Promise.resolve([]) : adminApi.drive.folders(folderId),
        adminApi.drive.files(params),
        adminApi.drive.usage().catch(() => null),
      ]);
      setFolders(fo);
      setFiles(fi);
      if (us) setUsage(us);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el Drive");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    adminApi.drive.me().then((d) => { setMe(d); setDenied(false); }).catch((e) => {
      if (e?.response?.status === 403) setDenied(true);
    });
  }, []);
  useEffect(() => {
    if (denied) { setLoading(false); return; }
    load(cur, search.trim(), tipoFilter);
    // eslint-disable-next-line
  }, [cur, tipoFilter, denied]);

  const refresh = () => load(cur, search.trim(), tipoFilter);

  const openFolder = (f) => { setSearch(""); setPath((p) => [...p, { id: f.id, nombre: f.nombre }]); };
  const goto = (i) => { setSearch(""); setPath((p) => p.slice(0, i + 1)); };

  const doSearch = (e) => { e.preventDefault(); load(cur, search.trim(), tipoFilter); };

  // ---------- Upload ----------
  const handleFiles = async (fileList) => {
    const arr = Array.from(fileList);
    for (const file of arr) {
      const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setUploads((u) => [...u, { uid, name: file.name, pct: 0, done: false, error: false }]);
      try {
        const { upload_id } = await adminApi.drive.uploadInit();
        const total = Math.ceil(file.size / CHUNK) || 1;
        for (let i = 0; i < total; i++) {
          const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
          await adminApi.drive.uploadChunk(upload_id, i, blob);
          const pct = Math.round(((i + 1) / total) * 100);
          setUploads((u) => u.map((x) => (x.uid === uid ? { ...x, pct } : x)));
        }
        await adminApi.drive.uploadComplete({ upload_id, filename: file.name, nombre: file.name, folder_id: cur });
        setUploads((u) => u.map((x) => (x.uid === uid ? { ...x, pct: 100, done: true } : x)));
        setTimeout(() => setUploads((u) => u.filter((x) => x.uid !== uid)), 2500);
      } catch (e) {
        setUploads((u) => u.map((x) => (x.uid === uid ? { ...x, error: true } : x)));
        toast.error(`${file.name}: ${e?.response?.data?.detail || "error al subir"}`);
      }
    }
    refresh();
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (!me?.can_upload) return;
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  // ---------- Actions ----------
  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await adminApi.drive.createFolder(newFolderName.trim(), cur);
      setNewFolderOpen(false); setNewFolderName("");
      toast.success("Carpeta creada");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const doRename = async () => {
    const t = renameTarget;
    try {
      if (t.kind === "folder") await adminApi.drive.updateFolder(t.id, { nombre: renameValue.trim() });
      else await adminApi.drive.updateFile(t.id, { nombre: renameValue.trim() });
      setRenameTarget(null);
      toast.success("Renombrado");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const doDelete = async (item, kind) => {
    if (!window.confirm(`¿Eliminar "${item.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      if (kind === "folder") await adminApi.drive.removeFolder(item.id);
      else await adminApi.drive.removeFile(item.id);
      toast.success("Eliminado");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const openPreview = async (f) => {
    setPreview({ file: f, url: null, loading: true });
    try {
      const blob = await adminApi.drive.blob(f.id);
      const url = URL.createObjectURL(blob);
      setPreview({ file: f, url, loading: false });
    } catch (e) {
      setPreview(null);
      toast.error("No se pudo cargar la vista previa");
    }
  };
  const downloadFile = async (f) => {
    try {
      const blob = await adminApi.drive.blob(f.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = f.filename || f.nombre; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error("No se pudo descargar"); }
  };

  const canManagePerms = me?.is_admin;

  if (denied) {
    return (
      <div className="max-w-lg mx-auto text-center py-24" data-testid="drive-denied">
        <div className="w-16 h-16 rounded-2xl bg-[#0A1C30]/5 flex items-center justify-center mx-auto mb-4">
          <HardDrive className="w-8 h-8 text-[#0A1C30]" />
        </div>
        <h1 className="text-2xl font-extrabold text-[#0A1C30] mb-2">Supera Drive</h1>
        <p className="text-[#6B7688]">Aún no tienes acceso a esta sección. Solicítalo al Super Admin para que active tus permisos.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl" data-testid="drive-admin">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-11 h-11 rounded-2xl bg-[#0A1C30] flex items-center justify-center">
          <HardDrive className="w-6 h-6 text-gold" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-[#0A1C30] leading-tight">Supera Drive</h1>
          <p className="text-sm text-[#6B7688]">Tu almacenamiento interno para videos, audios, imágenes y documentos.</p>
        </div>
      </div>

      <Tabs defaultValue="files" className="mt-5">
        <TabsList data-testid="drive-tabs">
          <TabsTrigger value="files" data-testid="drive-tab-files">Archivos</TabsTrigger>
          {canManagePerms && <TabsTrigger value="permisos" data-testid="drive-tab-permisos">Permisos del staff</TabsTrigger>}
        </TabsList>

        <TabsContent value="files" className="mt-4">
          {/* Usage bar */}
          {usage && (
            <div className="bg-white rounded-2xl border border-black/5 p-4 mb-4 flex items-center gap-4" data-testid="drive-usage">
              <div className="flex-1">
                <div className="flex justify-between text-sm font-bold text-[#0A1C30] mb-1">
                  <span>{fmtSize(usage.usado)} usados</span>
                  <span className="text-[#6B7688]">de 5 TB · {usage.archivos} archivos</span>
                </div>
                <div className="h-2.5 rounded-full bg-[#EEF1F5] overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[var(--gold)] to-[#C79A2E] rounded-full transition-all"
                       style={{ width: `${Math.min(100, Math.max(1.5, usage.pct))}%` }} />
                </div>
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {me?.can_create_folder && (
              <Button variant="outline" onClick={() => setNewFolderOpen(true)} data-testid="drive-new-folder-btn">
                <FolderPlus className="w-4 h-4 mr-2" /> Nueva carpeta
              </Button>
            )}
            {me?.can_upload && (
              <Button className="bg-[#0A1C30] hover:bg-[#0A1C30]/90" onClick={() => fileInput.current?.click()} data-testid="drive-upload-btn">
                <Upload className="w-4 h-4 mr-2" /> Subir archivos
              </Button>
            )}
            <input ref={fileInput} type="file" multiple hidden data-testid="drive-file-input"
                   onChange={(e) => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = ""; }} />

            <form onSubmit={doSearch} className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9AA3B2]" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar archivo…"
                     className="pl-9" data-testid="drive-search-input" />
            </form>

            <select value={tipoFilter} onChange={(e) => setTipoFilter(e.target.value)}
                    className="h-10 rounded-md border border-black/10 px-3 text-sm bg-white" data-testid="drive-type-filter">
              <option value="">Todos los tipos</option>
              <option value="imagen">Imágenes</option>
              <option value="video">Videos</option>
              <option value="audio">Audios</option>
              <option value="documento">Documentos</option>
              <option value="texto">Texto</option>
              <option value="comprimido">Comprimidos</option>
            </select>

            <div className="flex rounded-md border border-black/10 overflow-hidden">
              <button onClick={() => setView("grid")} className={`px-3 h-10 ${view === "grid" ? "bg-[#0A1C30] text-white" : "bg-white text-[#6B7688]"}`} data-testid="drive-view-grid"><LayoutGrid className="w-4 h-4" /></button>
              <button onClick={() => setView("list")} className={`px-3 h-10 ${view === "list" ? "bg-[#0A1C30] text-white" : "bg-white text-[#6B7688]"}`} data-testid="drive-view-list"><ListIcon className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Breadcrumb */}
          <div className="flex items-center flex-wrap gap-1 mb-3 text-sm" data-testid="drive-breadcrumb">
            {path.map((p, i) => (
              <React.Fragment key={p.id || "root"}>
                {i > 0 && <ChevronRight className="w-4 h-4 text-[#9AA3B2]" />}
                <button onClick={() => goto(i)} className={`px-2 py-1 rounded-lg font-bold ${i === path.length - 1 ? "text-[#0A1C30]" : "text-[#6B7688] hover:bg-black/5"}`}>
                  {p.nombre}
                </button>
              </React.Fragment>
            ))}
          </div>

          {/* Upload progress */}
          {uploads.length > 0 && (
            <div className="space-y-2 mb-4">
              {uploads.map((u) => (
                <div key={u.uid} className="bg-white rounded-xl border border-black/5 p-3" data-testid="drive-upload-progress">
                  <div className="flex justify-between text-xs font-bold text-[#0A1C30] mb-1">
                    <span className="truncate max-w-[70%]">{u.name}</span>
                    <span className={u.error ? "text-red-600" : "text-[#6B7688]"}>{u.error ? "Error" : u.done ? "Listo ✓" : `${u.pct}%`}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[#EEF1F5] overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${u.error ? "bg-red-500" : "bg-[#0A1C30]"}`} style={{ width: `${u.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Content */}
          <div onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
               className="min-h-[300px] rounded-2xl border-2 border-dashed border-transparent hover:border-black/5 transition-colors">
            {loading ? (
              <div className="flex items-center justify-center py-24 text-[#9AA3B2]"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : folders.length === 0 && files.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center text-[#9AA3B2]" data-testid="drive-empty">
                <HardDrive className="w-12 h-12 mb-3 opacity-40" />
                <p className="font-bold text-[#6B7688]">Esta carpeta está vacía</p>
                {me?.can_upload && <p className="text-sm">Arrastra archivos aquí o usa el botón «Subir archivos».</p>}
              </div>
            ) : view === "grid" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="drive-grid">
                {folders.map((f) => (
                  <FolderCard key={f.id} folder={f} me={me} onOpen={() => openFolder(f)}
                    onRename={() => { setRenameTarget({ ...f, kind: "folder" }); setRenameValue(f.nombre); }}
                    onDelete={() => doDelete(f, "folder")}
                    onMove={() => setMoveTarget({ ...f, kind: "folder" })} />
                ))}
                {files.map((f) => (
                  <FileCard key={f.id} file={f} me={me} onPreview={() => openPreview(f)} onDownload={() => downloadFile(f)}
                    onRename={() => { setRenameTarget({ ...f, kind: "file" }); setRenameValue(f.nombre); }}
                    onDelete={() => doDelete(f, "file")} onShare={() => setShareTarget(f)}
                    onMove={() => setMoveTarget({ ...f, kind: "file" })} />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-black/5 overflow-hidden" data-testid="drive-list">
                {folders.map((f) => (
                  <ListRow key={f.id} icon={Folder} accent="#0A1C30" name={f.nombre} sub="Carpeta" onClick={() => openFolder(f)}>
                    <RowMenu me={me} isFolder
                      onRename={() => { setRenameTarget({ ...f, kind: "folder" }); setRenameValue(f.nombre); }}
                      onDelete={() => doDelete(f, "folder")} onMove={() => setMoveTarget({ ...f, kind: "folder" })} />
                  </ListRow>
                ))}
                {files.map((f) => {
                  const Ic = fileIconFor(f.tipo);
                  return (
                    <ListRow key={f.id} icon={Ic} accent={ACCENT[f.tipo]} name={f.nombre}
                      sub={`${f.tipo} · ${fmtSize(f.size)}${f.share_enabled ? " · Compartido" : ""}`} onClick={() => openPreview(f)}>
                      <RowMenu me={me}
                        onPreview={() => openPreview(f)} onDownload={() => downloadFile(f)}
                        onRename={() => { setRenameTarget({ ...f, kind: "file" }); setRenameValue(f.nombre); }}
                        onDelete={() => doDelete(f, "file")} onShare={() => setShareTarget(f)}
                        onMove={() => setMoveTarget({ ...f, kind: "file" })} />
                    </ListRow>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {canManagePerms && (
          <TabsContent value="permisos" className="mt-4">
            <PermisosPanel />
          </TabsContent>
        )}
      </Tabs>

      {/* New folder dialog */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent data-testid="drive-new-folder-dialog">
          <DialogHeader><DialogTitle>Nueva carpeta</DialogTitle>
            <DialogDescription>Se creará dentro de «{path[path.length - 1].nombre}».</DialogDescription></DialogHeader>
          <Input autoFocus value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
                 placeholder="Nombre de la carpeta" onKeyDown={(e) => e.key === "Enter" && createFolder()} data-testid="drive-new-folder-name" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderOpen(false)}>Cancelar</Button>
            <Button className="bg-[#0A1C30]" onClick={createFolder} data-testid="drive-new-folder-save">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent data-testid="drive-rename-dialog">
          <DialogHeader><DialogTitle>Renombrar</DialogTitle><DialogDescription>Escribe el nuevo nombre.</DialogDescription></DialogHeader>
          <Input autoFocus value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && doRename()} data-testid="drive-rename-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>Cancelar</Button>
            <Button className="bg-[#0A1C30]" onClick={doRename} data-testid="drive-rename-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move dialog */}
      {moveTarget && <MoveDialog target={moveTarget} onClose={() => setMoveTarget(null)} onDone={() => { setMoveTarget(null); refresh(); }} />}

      {/* Share dialog */}
      {shareTarget && <ShareDialog file={shareTarget} onClose={() => setShareTarget(null)} onChanged={refresh} />}

      {/* Preview dialog */}
      {preview && <PreviewDialog preview={preview} onClose={() => { if (preview.url) URL.revokeObjectURL(preview.url); setPreview(null); }} onDownload={downloadFile} />}
    </div>
  );
}

function FolderCard({ folder, me, onOpen, onRename, onDelete, onMove }) {
  return (
    <div className="group relative bg-white rounded-2xl border border-black/5 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer" data-testid="drive-folder-card" onDoubleClick={onOpen}>
      <div className="flex items-start justify-between">
        <button onClick={onOpen} className="flex flex-col items-start text-left flex-1">
          <div className="w-12 h-12 rounded-xl bg-[#0A1C30]/5 flex items-center justify-center mb-3">
            <Folder className="w-7 h-7 text-[#0A1C30] fill-[#0A1C30]/10" />
          </div>
          <p className="font-bold text-sm text-[#0A1C30] line-clamp-2 break-words">{folder.nombre}</p>
          <p className="text-xs text-[#9AA3B2] mt-0.5">Carpeta</p>
        </button>
        <RowMenu me={me} isFolder onRename={onRename} onDelete={onDelete} onMove={onMove} onOpen={onOpen} />
      </div>
    </div>
  );
}

function FileCard({ file, me, onPreview, onDownload, onRename, onDelete, onShare, onMove }) {
  const Ic = fileIconFor(file.tipo);
  const accent = ACCENT[file.tipo] || ACCENT.archivo;
  return (
    <div className="group relative bg-white rounded-2xl border border-black/5 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid="drive-file-card">
      <button onClick={onPreview} className="block w-full text-left">
        <div className="h-24 flex items-center justify-center" style={{ background: `${accent}12` }}>
          <Ic className="w-9 h-9" style={{ color: accent }} />
        </div>
        <div className="p-3">
          <p className="font-bold text-sm text-[#0A1C30] line-clamp-1 break-all">{file.nombre}</p>
          <div className="flex items-center gap-1.5 mt-1">
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 uppercase" style={{ background: `${accent}18`, color: accent }}>{file.ext}</Badge>
            <span className="text-xs text-[#9AA3B2]">{fmtSize(file.size)}</span>
            {file.share_enabled && <Link2 className="w-3.5 h-3.5 text-emerald-600" />}
          </div>
        </div>
      </button>
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <RowMenu me={me} onPreview={onPreview} onDownload={onDownload} onRename={onRename} onDelete={onDelete} onShare={onShare} onMove={onMove} light />
      </div>
    </div>
  );
}

function ListRow({ icon: Icon, accent, name, sub, onClick, children }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-black/5 last:border-0 hover:bg-black/[0.02]">
      <button onClick={onClick} className="flex items-center gap-3 flex-1 text-left min-w-0">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${accent}14` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
        <div className="min-w-0">
          <p className="font-bold text-sm text-[#0A1C30] truncate">{name}</p>
          <p className="text-xs text-[#9AA3B2] capitalize">{sub}</p>
        </div>
      </button>
      {children}
    </div>
  );
}

function RowMenu({ me, isFolder, onOpen, onPreview, onDownload, onRename, onDelete, onShare, onMove, light }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button onClick={(e) => e.stopPropagation()} className={`p-1.5 rounded-lg ${light ? "bg-white/90 shadow-sm" : "hover:bg-black/5"}`} data-testid="drive-item-menu">
          <MoreVertical className="w-4 h-4 text-[#6B7688]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {onOpen && isFolder && <DropdownMenuItem onClick={onOpen}><Folder className="w-4 h-4 mr-2" /> Abrir</DropdownMenuItem>}
        {onPreview && <DropdownMenuItem onClick={onPreview} data-testid="drive-menu-preview"><Eye className="w-4 h-4 mr-2" /> Vista previa</DropdownMenuItem>}
        {onDownload && <DropdownMenuItem onClick={onDownload}><Download className="w-4 h-4 mr-2" /> Descargar</DropdownMenuItem>}
        {onShare && me?.can_share && <DropdownMenuItem onClick={onShare} data-testid="drive-menu-share"><Share2 className="w-4 h-4 mr-2" /> Compartir</DropdownMenuItem>}
        <DropdownMenuItem onClick={onRename}><Pencil className="w-4 h-4 mr-2" /> Renombrar</DropdownMenuItem>
        {me?.can_move && <DropdownMenuItem onClick={onMove}><FolderInput className="w-4 h-4 mr-2" /> Mover</DropdownMenuItem>}
        {me?.can_delete && <><DropdownMenuSeparator /><DropdownMenuItem onClick={onDelete} className="text-red-600 focus:text-red-600" data-testid="drive-menu-delete"><Trash2 className="w-4 h-4 mr-2" /> Eliminar</DropdownMenuItem></>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MoveDialog({ target, onClose, onDone }) {
  const [all, setAll] = useState([]);
  const [dest, setDest] = useState("");
  useEffect(() => { adminApi.drive.foldersAll().then(setAll).catch(() => {}); }, []);
  const move = async () => {
    try {
      if (target.kind === "folder") await adminApi.drive.updateFolder(target.id, { parent_id: dest });
      else await adminApi.drive.updateFile(target.id, { folder_id: dest });
      toast.success("Movido");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const options = all.filter((f) => f.id !== target.id);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="drive-move-dialog">
        <DialogHeader><DialogTitle>Mover «{target.nombre}»</DialogTitle><DialogDescription>Elige la carpeta destino.</DialogDescription></DialogHeader>
        <div className="max-h-72 overflow-y-auto space-y-1 border border-black/5 rounded-xl p-2">
          <button onClick={() => setDest("")} className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold ${dest === "" ? "bg-[#0A1C30] text-white" : "hover:bg-black/5 text-[#0A1C30]"}`}>
            <HardDrive className="w-4 h-4" /> Supera Drive (raíz)
          </button>
          {options.map((f) => (
            <button key={f.id} onClick={() => setDest(f.id)} className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold ${dest === f.id ? "bg-[#0A1C30] text-white" : "hover:bg-black/5 text-[#0A1C30]"}`}>
              <Folder className="w-4 h-4" /> {f.nombre}
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-[#0A1C30]" onClick={move} data-testid="drive-move-confirm">Mover aquí</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShareDialog({ file, onClose, onChanged }) {
  const [enabled, setEnabled] = useState(!!file.share_enabled);
  const [token, setToken] = useState(file.share_token || "");
  const [copied, setCopied] = useState(false);
  const url = token ? `${window.location.origin}/d/${token}` : "";
  const toggle = async (val) => {
    try {
      const res = await adminApi.drive.share(file.id, val);
      setEnabled(res.share_enabled); setToken(res.share_token);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const copy = () => { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="drive-share-dialog">
        <DialogHeader><DialogTitle>Compartir «{file.nombre}»</DialogTitle>
          <DialogDescription>Genera un enlace público con vista previa y branding Supera.</DialogDescription></DialogHeader>
        <div className="flex items-center justify-between bg-[#F4F6F9] rounded-xl p-3">
          <div>
            <p className="font-bold text-sm text-[#0A1C30]">Enlace público</p>
            <p className="text-xs text-[#6B7688]">{enabled ? "Cualquiera con el enlace puede verlo" : "Enlace desactivado"}</p>
          </div>
          <Switch checked={enabled} onCheckedChange={toggle} data-testid="drive-share-switch" />
        </div>
        {enabled && url && (
          <div className="flex items-center gap-2">
            <Input readOnly value={url} className="text-xs" data-testid="drive-share-url" />
            <Button variant="outline" size="icon" onClick={copy} data-testid="drive-share-copy">
              {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewDialog({ preview, onClose, onDownload }) {
  const { file, url, loading } = preview;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="drive-preview-dialog">
        <DialogHeader>
          <DialogTitle className="truncate pr-8">{file.nombre}</DialogTitle>
          <DialogDescription>{file.tipo} · {fmtSize(file.size)}</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl bg-[#0A1C30]/5 flex items-center justify-center min-h-[220px] max-h-[65vh] overflow-auto p-2">
          {loading || !url ? (
            <Loader2 className="w-8 h-8 animate-spin text-[#9AA3B2]" />
          ) : file.tipo === "imagen" ? (
            <img src={url} alt={file.nombre} className="max-w-full max-h-[60vh] object-contain rounded-lg" />
          ) : file.tipo === "video" ? (
            <video src={url} controls className="max-w-full max-h-[60vh] rounded-lg" />
          ) : file.tipo === "audio" ? (
            <audio src={url} controls className="w-full" />
          ) : file.ext === "pdf" ? (
            <iframe src={url} title={file.nombre} className="w-full h-[60vh] rounded-lg bg-white" />
          ) : (
            <div className="text-center py-10 text-[#6B7688]">
              <FileIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No hay vista previa para este tipo de archivo.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button className="bg-[#0A1C30]" onClick={() => onDownload(file)}><Download className="w-4 h-4 mr-2" /> Descargar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PERM_LABELS = [
  ["can_upload", "Subir archivos"],
  ["can_create_folder", "Crear carpetas"],
  ["can_share", "Compartir enlaces"],
  ["can_move", "Mover archivos"],
  ["can_delete", "Eliminar"],
];

function PermisosPanel() {
  const [staff, setStaff] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, f] = await Promise.all([adminApi.drive.staff(), adminApi.drive.foldersAll()]);
      setStaff(s); setFolders(f);
    } catch (e) { toast.error("No se pudo cargar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const update = (email, patch) => setStaff((st) => st.map((s) => (s.email === email ? { ...s, ...patch } : s)));
  const save = async (s) => {
    setSaving(s.email);
    try {
      await adminApi.drive.setStaff(s.email, {
        enabled: s.enabled, allowed_folders: s.allowed_folders || [],
        can_upload: s.can_upload, can_create_folder: s.can_create_folder,
        can_share: s.can_share, can_move: s.can_move, can_delete: s.can_delete,
      });
      toast.success(`Permisos guardados para ${s.nombre}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(""); }
  };
  const toggleFolder = (email, fid) => {
    setStaff((st) => st.map((s) => {
      if (s.email !== email) return s;
      const set = new Set(s.allowed_folders || []);
      set.has(fid) ? set.delete(fid) : set.add(fid);
      return { ...s, allowed_folders: Array.from(set) };
    }));
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#9AA3B2]" /></div>;

  return (
    <div className="space-y-4" data-testid="drive-permisos-panel">
      <div className="flex items-center gap-2 text-sm text-[#6B7688] bg-[#0A1C30]/5 rounded-xl p-3">
        <Shield className="w-4 h-4 text-[#0A1C30]" />
        Define qué puede hacer cada miembro del staff en Supera Drive. Puedes limitar el acceso a carpetas específicas.
      </div>
      {staff.length === 0 && <p className="text-sm text-[#9AA3B2] py-8 text-center">Aún no hay miembros del staff. Créalos en «Gestión Chat».</p>}
      {staff.map((s) => (
        <div key={s.email} className="bg-white rounded-2xl border border-black/5 p-4" data-testid="drive-staff-row">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-extrabold text-[#0A1C30]">{s.nombre}</p>
              <p className="text-xs text-[#9AA3B2]">{s.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-[#6B7688]">Acceso</span>
              <Switch checked={s.enabled} onCheckedChange={(v) => update(s.email, { enabled: v })} data-testid="drive-staff-enabled" />
            </div>
          </div>
          {s.enabled && (
            <>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mb-3">
                {PERM_LABELS.map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-sm text-[#0A1C30] cursor-pointer">
                    <Checkbox checked={!!s[key]} onCheckedChange={(v) => update(s.email, { [key]: !!v })} />
                    {label}
                  </label>
                ))}
              </div>
              <div className="border-t border-black/5 pt-3">
                <p className="text-xs font-bold text-[#6B7688] mb-2">Carpetas permitidas <span className="font-normal">(vacío = acceso a todo)</span></p>
                <div className="flex flex-wrap gap-2">
                  {folders.length === 0 && <span className="text-xs text-[#9AA3B2]">No hay carpetas creadas todavía.</span>}
                  {folders.map((f) => {
                    const on = (s.allowed_folders || []).includes(f.id);
                    return (
                      <button key={f.id} onClick={() => toggleFolder(s.email, f.id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${on ? "bg-[#0A1C30] text-white border-[#0A1C30]" : "bg-white text-[#6B7688] border-black/10 hover:border-[#0A1C30]/40"}`}>
                        <Folder className="w-3.5 h-3.5" /> {f.nombre}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
          <div className="flex justify-end mt-3">
            <Button size="sm" className="bg-[#0A1C30]" disabled={saving === s.email} onClick={() => save(s)} data-testid="drive-staff-save">
              {saving === s.email ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
